import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Network } from 'lucide-react'
import { tunnelsApi, devicesApi } from '../api/client'
import type { Tunnel, Device } from '../types'
import clsx from 'clsx'

function TunnelStatusBadge({ status }: { status: Tunnel['status'] }) {
  const config = {
    ready: { color: 'bg-green-500/10 text-green-400', label: 'Ready' },
    connecting: { color: 'bg-yellow-500/10 text-yellow-400', label: 'Connecting' },
    pending: { color: 'bg-blue-500/10 text-blue-400', label: 'Pending' },
    error: { color: 'bg-red-500/10 text-red-400', label: 'Error' },
    disconnected: { color: 'bg-slate-500/10 text-slate-400', label: 'Disconnected' },
  }
  const { color, label } = config[status]

  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded', color)}>
      {label}
    </span>
  )
}

export default function Tunnels() {
  const { data: tunnelsData, isLoading: tunnelsLoading } = useQuery({
    queryKey: ['tunnels'],
    queryFn: () => tunnelsApi.list(),
    refetchInterval: 5000,
  })

  const { data: devicesData } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
  })

  const tunnels: Tunnel[] = tunnelsData?.tunnels ?? []
  const devices: Device[] = devicesData?.devices ?? []
  const deviceMap = new Map(devices.map((d) => [d.id, d]))

  if (tunnelsLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 w-32 bg-slate-700 rounded mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-800 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Tunnels</h1>

      {tunnels.length === 0 ? (
        <div className="card text-center py-12">
          <Network className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">No tunnels configured</p>
          <p className="text-slate-500 text-sm mt-1">
            Create tunnels from the device detail page
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                <th className="pb-4 font-medium">Name</th>
                <th className="pb-4 font-medium">Device</th>
                <th className="pb-4 font-medium">Mode</th>
                <th className="pb-4 font-medium">Target</th>
                <th className="pb-4 font-medium">Status</th>
                <th className="pb-4 font-medium">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {tunnels.map((tunnel) => {
                const device = deviceMap.get(tunnel.device_id)
                return (
                  <tr key={tunnel.id} className="hover:bg-slate-800/50">
                    <td className="py-4">
                      <span className="text-white font-medium">
                        {tunnel.name || `${tunnel.mode} Tunnel`}
                      </span>
                    </td>
                    <td className="py-4">
                      <Link
                        to={`/devices/${tunnel.device_id}`}
                        className="text-primary-400 hover:text-primary-300"
                      >
                        {device?.name || device?.hostname || 'Unknown'}
                      </Link>
                    </td>
                    <td className="py-4">
                      <span className="px-2 py-1 text-xs bg-slate-700 rounded text-slate-300">
                        {tunnel.mode}
                      </span>
                    </td>
                    <td className="py-4 text-slate-300">
                      {tunnel.target_addr}:{tunnel.target_port}
                    </td>
                    <td className="py-4">
                      <TunnelStatusBadge status={tunnel.status} />
                    </td>
                    <td className="py-4">
                      {tunnel.enabled ? (
                        <span className="text-green-400">Yes</span>
                      ) : (
                        <span className="text-slate-500">No</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
